import { useState, useEffect } from "react";
import { useTrackableState } from "../service/PageService";
import {
  Button,
  Stack,
  Typography,
  Box,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Container,
} from "@mui/material";

import "./FormulaMatrix.scss";
import FormulaCardField from "../components/CardField/FormulaCardField";
import CardField from "../components/CardField/CardField";

const Component = ({ registry, events, choice, options, asset, ...args }) => {
  const [title, setTitle] = useTrackableState(args.title || "");
  const [subtitle, setSubtitle] = useTrackableState(args.subtitle || "");
  const [visible, setVisible] = useTrackableState(true);
  const [formula, setFormula] = useTrackableState();
  const [formulaDetails, setFormulaDetails] = useTrackableState({});
  const [maxIndemn, setMaxIndemn] = useTrackableState(null);
  const [gpcOption, setGpcOption] = useTrackableState(null);
  const [contenuEquip, setContenuEquip] = useTrackableState(null);
  const [setLoading] = useState(false);
  const [affined, setAffined] = useState(false);
  const [devis, setDevis] = useState(false);

  const [formula1, setFormula1] = useTrackableState(args.formula1 || {});
  const [formula2, setFormula2] = useTrackableState(args.formula2 || {});
  const [formula3, setFormula3] = useTrackableState(args.formula2 || {});

  const [assuranceOptions, setAssuranceOptions] = useTrackableState(
    args.assuranceOptions || []
  );
  const [franchises, setFranchises] = useTrackableState(args.franchises || []);
  const [plafonds, setPlafonds] = useTrackableState(args.plafonds || []);
  const [assistances, setAssistances] = useTrackableState(
    args.assistances || []
  );
  const [bdgOptions, setBdgOptions] = useTrackableState(args.bdgOptions || []);
  const [forfaitKMOptions, setForfaitKMOptions] = useTrackableState(
    args.forfaitKMOptions || []
  );
  const [conducExclNovOptions, setConducExclNovOptions] = useTrackableState(
    args.conducExclNovOptions || []
  );
  const [conducExclNonNovOptions, setConducExclNonNovOptions] =
    useTrackableState(args.conducExclNonNovOptions || []);
  const [gpcOptions, setGpcOptions] = useTrackableState(args.gpcOptions || []);
  const [contenuEquipVehOptions, setContenuEquipVehOptions] = useTrackableState(
    args.contenuEquipVehOptions || []
  );

  const formulas = [formula1, formula2, formula3];

  const yes = () => {
    return (
      <svg
        width="17"
        height="13"
        viewBox="0 0 17 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.95188 12.7496C6.79916 12.9103 6.59078 13 6.37432 13C6.15787 13 5.94949 12.9103 5.79676 12.7496L1.18421 7.92228C0.705525 7.42139 0.705525 6.60918 1.18421 6.10923L1.76177 5.50473C2.2406 5.00384 3.01592 5.00384 3.4946 5.50473L6.37432 8.51818L14.1558 0.375664C14.6346 -0.125221 15.4107 -0.125221 15.8886 0.375664L16.4662 0.980169C16.9448 1.48105 16.9448 2.29311 16.4662 2.79322L6.95188 12.7496Z"
          fill="#0DDFB5"
        />
      </svg>
    );
  };

  const no = () => {
    return (
      <svg
        width="14"
        height="13"
        viewBox="0 0 14 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_2681_5033)">
          <path
            d="M1.55753 12.9186C1.23881 12.9371 0.925357 12.8311 0.683273 12.623C0.205836 12.1428 0.205836 11.3671 0.683273 10.8868L11.1376 0.432464C11.6342 -0.0322 12.4134 -0.00636964 12.878 0.490207C13.2982 0.939259 13.3227 1.62946 12.9354 2.10714L2.41947 12.623C2.1805 12.8281 1.87208 12.9339 1.55753 12.9186Z"
            fill="#FF8283"
          />
          <path
            d="M11.9991 12.9186C11.6761 12.9172 11.3665 12.789 11.1372 12.5615L0.682807 2.1071C0.240487 1.59057 0.300623 0.813226 0.81715 0.370865C1.27816 -0.0239309 1.95807 -0.0239309 2.41904 0.370865L12.9349 10.8252C13.4314 11.29 13.4571 12.0692 12.9923 12.5657C12.9738 12.5854 12.9547 12.6045 12.9349 12.623C12.6774 12.847 12.3386 12.954 11.9991 12.9186Z"
            fill="#FF8283"
          />
        </g>
        <defs>
          <clipPath id="clip0_2681_5033">
            <rect
              width="13"
              height="13"
              fill="white"
              transform="translate(0.325195)"
            />
          </clipPath>
        </defs>
      </svg>
    );
  };

  useEffect(() => {
    setFormulaDetails((prevDetails) => ({
      ...prevDetails,
      gpc: gpcOption === 2 ? maxIndemn + 5 : maxIndemn,
    }));
  }, [gpcOption, maxIndemn]);

  if (args.code) {
    const api = {};

    Object.defineProperties(api, {
      visible: {
        get: () => visible,
        set: (v) => setVisible(v),
      },
      title: {
        get: () => title,
        set: (v) => setTitle(v),
      },
      subtitle: {
        get: () => subtitle,
        set: (v) => setSubtitle(v),
      },
      value: {
        get: () => formulaDetails,
        set: (v) => setFormulaDetails(v),
      },
      affined: {
        get: () => affined,
        set: (v) => setAffined(v),
      },
      devis: {
        get: () => devis,
        set: (v) => setDevis(v),
      },
      formula1: {
        get: () => formula1,
        set: (v) => setFormula1(v),
      },
      formula2: {
        get: () => formula2,
        set: (v) => setFormula2(v),
      },
      formula3: {
        get: () => formula3,
        set: (v) => setFormula3(v),
      },
      assuranceOptions: {
        get: () => assuranceOptions,
        set: (v) => setAssuranceOptions(v),
      },
      franchises: {
        get: () => franchises,
        set: (v) => setFranchises(v),
      },
      plafonds: {
        get: () => plafonds,
        set: (v) => setPlafonds(v),
      },
      assistances: {
        get: () => assistances,
        set: (v) => setAssistances(v),
      },
      bdgOptions: {
        get: () => bdgOptions,
        set: (v) => setBdgOptions(v),
      },
      forfaitKMOptions: {
        get: () => forfaitKMOptions,
        set: (v) => setForfaitKMOptions(v),
      },
      conducExclNovOptions: {
        get: () => conducExclNovOptions,
        set: (v) => setConducExclNovOptions(v),
      },
      conducExclNonNovOptions: {
        get: () => conducExclNonNovOptions,
        set: (v) => setConducExclNonNovOptions(v),
      },
      gpcOptions: {
        get: () => gpcOptions,
        set: (v) => setGpcOptions(v),
      },
      contenuEquipVehOptions: {
        get: () => contenuEquipVehOptions,
        set: (v) => setContenuEquipVehOptions(v),
      },
    });

    registry.set(args.code, api);
  }

  if (!visible) return <></>;

  const handleAffinerTarif = async (f) => {
    setLoading(true);
    setFormula(f.id);
    setFormulaDetails({
      id: f.id,
      name: f.name,
      prime: f.prime,
      formulaOptions: f.formulaOptions,
      gpc: f.gpc,
      assistance: f.assistance,
      bdg: f.bdg,
      conducExclNonNov: f.conducExclNonNov,
      conducExclNov: f.conducExclNov,
      forfaitKM: f.forfaitKM,
      franchiseVIDom: f.franchiseVIDom,
      formulaSupp: {
        remorque: "2",
        indemnisationPlus: "1",
        contenuEquipVeh: "1",
      },
      action: {
        afficherDevis: false,
      },
    });
    setAffined(true);
    setLoading(false);
  };

  return (
    <Stack direction={"column"} spacing={2} alignItems={"center"}>
      {!affined ? (
        <div style={{ marginTop: 50 }}>
          <Stack direction={"row"} spacing={2}>
            <Stack direction={"column"}>
              <div style={{ marginTop: 139, minWidth: "245px" }}>
                {assuranceOptions?.map((option, index) => (
                  <Stack direction={"column"} key={index} spacing={2}>
                    <div>{option}</div>
                    <div>{index < assuranceOptions?.length - 1 && <hr />}</div>
                  </Stack>
                ))}
              </div>
            </Stack>
            {formulas?.map((formula, index) => (
              <FormulaCardField
                key={index}
                selected={false}
                recommended={formula.id === 2}
                className={"FormulaItem"}
              >
                <Stack
                  direction={"column"}
                  spacing={2}
                  justifyContent="center"
                  style={{ minWidth: "176px" }}
                >
                  <Stack direction={"column"} spacing={1}>
                    <Typography style={{ marginBottom: 5, fontSize: "12px" }}>
                      {formula.name}
                    </Typography>
                    <Typography style={{ fontSize: "10px" }}>
                      à partir de
                    </Typography>
                    <Stack direction={"row"} justifyContent="center">
                      <Typography style={{ fontSize: "16px" }}>
                        {formula.prime}€
                      </Typography>
                      <Typography style={{ fontSize: "12px" }}>
                        /mois
                      </Typography>
                    </Stack>
                  </Stack>
                  {assuranceOptions?.map((option, optionIndex) => (
                    <div key={optionIndex}>
                      <Box alignItems={"center"}>
                        {formula.formulaOptions &&
                        formula.formulaOptions.includes(option)
                          ? yes()
                          : no()}
                      </Box>
                      {optionIndex < assuranceOptions?.length - 1 && <hr />}
                    </div>
                  ))}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ fontSize: "12px" }}
                    onClick={() => handleAffinerTarif(formula)}
                  >
                    Affinez votre tarif
                  </Button>
                </Stack>
              </FormulaCardField>
            ))}
          </Stack>
        </div>
      ) : (
        formula &&
        !devis && (
          <Stack direction={"column"} spacing={1}>
            <CardField selected={false} className={"FoundedCarItem"}>
              <Stack direction={"column"} spacing={3}>
                <Container>
                  <Stack
                    direction={"row"}
                    spacing={10}
                    justifyContent={"flex-end"}
                  >
                    <Stack direction={"column"}>
                      <Typography style={{ fontSize: "20px" }}>
                        {formulaDetails.name}
                      </Typography>
                      <Stack direction={"row"} justifyContent="center">
                        <Typography
                          color={"error"}
                          style={{
                            fontSize: "16px",
                            textDecoration: "line-through",
                          }}
                        >
                          {formulaDetails.prime + 2}€
                        </Typography>
                        <Typography
                          color={"error"}
                          style={{
                            fontSize: "12px",
                            textDecoration: "line-through",
                          }}
                        >
                          /mois
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent="center">
                        <Typography style={{ fontSize: "16px" }}>
                          {formulaDetails.prime}€
                        </Typography>
                        <Typography style={{ fontSize: "12px" }}>
                          /mois
                        </Typography>
                      </Stack>
                    </Stack>

                    <Button
                      variant={"outlined"}
                      color={"info"}
                      onClick={() => {
                        setAffined(false);
                        setFormula({});
                      }}
                    >
                      Modifier
                    </Button>
                  </Stack>
                </Container>
                <Divider variant="middle" />

                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>
                    Vos garanties de base
                  </div>
                  <Container>
                    {formulaDetails.formulaOptions.map((g, index) => (
                      <Stack
                        direction={"row"}
                        spacing={1}
                        justifyContent={"left"}
                        key={index}
                      >
                        <div className={"FoundedCarItem__Title2"}>{g} :</div>
                        <Box flexGrow={1} />
                        <div
                          className={"FoundedCarItem__Title2"}
                          style={{ textDecoration: "underline" }}
                        >
                          Plus de details
                        </div>
                      </Stack>
                    ))}
                  </Container>
                </Stack>
                <Divider variant="middle" />
                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>
                    Vol et incendie
                  </div>
                  <Stack direction={"row"} spacing={1}>
                    {franchises?.map((f, index) => (
                      <Button
                        variant="contained"
                        style={{ width: "fit-content" }}
                        key={index}
                        size="small"
                        onClick={() =>
                          setFormulaDetails((prevDetails) => ({
                            ...prevDetails,
                            franchiseVIDom: f.code,
                          }))
                        }
                      >
                        {f.libelle}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
                <Divider variant="middle" />
                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>Bris de glace</div>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    justifyContent={"center"}
                  >
                    {bdgOptions?.map((o, index) => (
                      <Button
                        variant="contained"
                        style={{ width: "fit-content" }}
                        key={index}
                        size="small"
                        onClick={() =>
                          setFormulaDetails((prevDetails) => ({
                            ...prevDetails,
                            bdg: o.code,
                          }))
                        }
                      >
                        {o.libelle}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
                <Divider variant="middle" />
                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>
                    Protection du conducteur
                  </div>
                  <div className={"FoundedCarItem__Title2"}>
                    Cette garantie vous permet d'être indemnisé de vos dommages
                    corporels subis en tant que conducteur
                  </div>
                  <div className={"FoundedCarItem__Title1"}>
                    Choisissez le plafond maximal d’indemnisation
                  </div>
                  <Stack
                    direction={"column"}
                    justifyContent={"center"}
                    spacing={1}
                  >
                    <Stack
                      direction={"row"}
                      spacing={1}
                      justifyContent={"center"}
                    >
                      {plafonds?.map((p, index) => (
                        <Button
                          key={index}
                          style={{ width: "fit-content" }}
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setMaxIndemn(p.code);
                          }}
                        >
                          {p.libelle}
                        </Button>
                      ))}
                    </Stack>
                    <FormControl>
                      <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="radio-buttons-group"
                        defaultValue={1}
                      >
                        {gpcOptions?.map((o, index) => (
                          <FormControlLabel
                            key={index}
                            value={o.code}
                            control={<Radio size="small" />}
                            onChange={() => {
                              setGpcOption(o.code);
                            }}
                            label={
                              <Stack direction={"row"} spacing={2}>
                                <div variant="h5">{o.libelle}</div>
                                {o.eco && (
                                  <div className={"FoundedCarItem__Title1"}>
                                    {o.eco} € par mois d’économie
                                  </div>
                                )}
                              </Stack>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                </Stack>
                <Divider variant="middle" />
                <Stack justifyContent="center" spacing={2}>
                  <Typography color={"error"}>
                    Cette garantie s’applique dans le cas d’un accident de la
                    route responsable ou dans le cas de perte de contrôle de
                    votre voiture sans tiers impliqué.
                  </Typography>
                  <div
                    className={"FoundedCarItem__Title2"}
                    style={{ textDecoration: "underline" }}
                  >
                    Comprendre le mécanisme d’indemnisation
                  </div>
                </Stack>
                <Divider variant="middle" />
                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>Assistance</div>
                  <Stack direction={"row"} justifyContent="center" spacing={1}>
                    <Typography>Assistance</Typography>
                    <div
                      className={"FoundedCarItem__Title2"}
                      style={{ textDecoration: "underline" }}
                    >
                      Plus de détail
                    </div>
                  </Stack>
                  <Stack direction={"row"} spacing={1} justifyContent="center">
                    {assistances?.map((a, index) => (
                      <Button
                        key={index}
                        variant="contained"
                        style={{ width: "fit-content" }}
                        size="small"
                        onClick={() =>
                          setFormulaDetails((prevDetails) => ({
                            ...prevDetails,
                            assistance: a.code,
                          }))
                        }
                      >
                        {a.libelle}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
                <Divider variant="middle" />
                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>
                    Vous roulez moins de 8000 KM par an ?
                  </div>
                  <Stack direction={"row"} justifyContent="center" spacing={1}>
                    <Typography>
                      Vous pouvez mettre en place un forfait kilométrique
                    </Typography>
                  </Stack>
                  <Stack
                    direction={"column"}
                    justifyContent="center"
                    spacing={1}
                  >
                    <FormControl>
                      <FormLabel id="demo-radio-buttons-group-label">
                        Choix de votre option :
                      </FormLabel>
                      <Container>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label"
                          defaultValue="female"
                          name="radio-buttons-group"
                        >
                          {forfaitKMOptions?.map((f, index) => (
                            <FormControlLabel
                              key={index}
                              value={f.code}
                              control={<Radio size="small" />}
                              onChange={() =>
                                setFormulaDetails((prevDetails) => ({
                                  ...prevDetails,
                                  forfaitKM: f.code,
                                }))
                              }
                              label={
                                <Stack direction={"row"} spacing={2}>
                                  <div variant="h5">{f.libelle}</div>
                                  <Box flexGrow={1} />
                                  {f.eco && (
                                    <div className={"FoundedCarItem__Title1"}>
                                      {f.eco} € par mois d’économie
                                    </div>
                                  )}
                                </Stack>
                              }
                            />
                          ))}
                        </RadioGroup>
                      </Container>
                    </FormControl>
                    <div
                      className={"FoundedCarItem__Title2"}
                      style={{ textDecoration: "underline" }}
                    >
                      Comprendre le mécanisme de la forfait kilomètrique
                    </div>
                  </Stack>
                </Stack>
                <Divider variant="middle" />
                <Stack direction={"column"} spacing={3}>
                  <div className={"FoundedCarItem__Title1"}>
                    Option de conduite exclusive
                  </div>
                  <Stack direction={"row"} justifyContent="center" spacing={1}>
                    <Typography>
                      Seulement vous et votre conjoint, concubin, pacsé
                      conduisez ?Faîte des économies en choisissant une option
                      conduite exclusive :
                    </Typography>
                  </Stack>
                  <Stack
                    direction={"column"}
                    justifyContent="center"
                    spacing={1}
                  >
                    <FormControl>
                      <FormLabel id="demo-radio-buttons-group-label">
                        En cas de prêt de véhicule à un conducteur non novice :
                      </FormLabel>
                      <Container>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label"
                          defaultValue="female"
                          name="radio-buttons-group"
                        >
                          {conducExclNonNovOptions?.map((o, index) => (
                            <FormControlLabel
                              key={index}
                              value={o.code}
                              control={<Radio size="small" />}
                              onChange={() =>
                                setFormulaDetails((prevDetails) => ({
                                  ...prevDetails,
                                  conducExclNov: o.code,
                                }))
                              }
                              label={
                                <Stack direction={"row"} spacing={2}>
                                  <div variant="h5">{o.libelle}</div>
                                  <Box flexGrow={1} />
                                  {o.eco && (
                                    <div className={"FoundedCarItem__Title1"}>
                                      {o.eco} € par mois d’économie
                                    </div>
                                  )}
                                </Stack>
                              }
                            />
                          ))}
                        </RadioGroup>
                      </Container>
                    </FormControl>
                    <FormControl>
                      <FormLabel id="demo-radio-buttons-group-label">
                        En cas de prêt de véhicule à un conducteur novice :
                      </FormLabel>
                      <Container>
                        <RadioGroup
                          aria-labelledby="demo-radio-buttons-group-label"
                          defaultValue="female"
                          name="radio-buttons-group"
                        >
                          {conducExclNovOptions?.map((o, index) => (
                            <FormControlLabel
                              key={index}
                              value={o.code}
                              control={<Radio size="small" />}
                              onChange={() =>
                                setFormulaDetails((prevDetails) => ({
                                  ...prevDetails,
                                  conducExclNonNov: o.code,
                                }))
                              }
                              label={
                                <Stack direction={"row"} spacing={2}>
                                  <div variant="h5">{o.libelle}</div>
                                  {o.eco && (
                                    <div className={"FoundedCarItem__Title1"}>
                                      {o.eco} € par mois d’économie
                                    </div>
                                  )}
                                </Stack>
                              }
                            />
                          ))}
                        </RadioGroup>
                      </Container>
                    </FormControl>
                    <div
                      className={"FoundedCarItem__Title2"}
                      style={{ textDecoration: "underline" }}
                    >
                      Comprendre le mécanisme de la conduite exclusive
                    </div>
                  </Stack>
                </Stack>
              </Stack>
            </CardField>
            <CardField className={"SuggestionItem"} selected={false}>
              <Stack
                direction={"row"}
                spacing={5}
                alignItems={"baseline"}
                justifyContent={"center"}
              >
                <Stack direction={"row"} spacing={1}>
                  <svg
                    width="41"
                    height="42"
                    viewBox="0 0 41 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_2682_5264)">
                      <path
                        d="M40.1458 34.8634H35.875V8.38428C35.875 4.1459 32.4259 0.696777 28.1875 0.696777H7.6875C3.44913 0.696777 0 4.1459 0 8.38428V28.8843C0 32.8544 3.02375 36.1293 6.888 36.5308C7.28604 39.4452 9.79046 41.6968 12.8125 41.6968C15.8345 41.6968 18.315 39.4657 18.7302 36.5718H40.1458C40.6173 36.5718 41 36.1891 41 35.7176C41 35.2461 40.6173 34.8634 40.1458 34.8634ZM7.6875 2.40511H28.1875C31.4846 2.40511 34.1667 5.08719 34.1667 8.38428V22.9051H29.0417V8.38428C29.0417 7.91278 28.659 7.53011 28.1875 7.53011C27.716 7.53011 27.3333 7.91278 27.3333 8.38428V22.9051H22.2083V8.38428C22.2083 7.91278 21.8257 7.53011 21.3542 7.53011C20.8827 7.53011 20.5 7.91278 20.5 8.38428V22.9051H15.375V8.38428C15.375 7.91278 14.9923 7.53011 14.5208 7.53011C14.0493 7.53011 13.6667 7.91278 13.6667 8.38428V22.9051H8.54167V8.38428C8.54167 7.91278 8.159 7.53011 7.6875 7.53011C7.216 7.53011 6.83333 7.91278 6.83333 8.38428V22.9051H1.70833V8.38428C1.70833 5.08719 4.39042 2.40511 7.6875 2.40511ZM1.70833 28.8843V24.6134H34.1667V34.8634H18.7302C18.315 31.9695 15.8192 29.7384 12.8125 29.7384C9.82463 29.7384 7.339 31.9439 6.90167 34.8122C3.97529 34.4261 1.70833 31.9149 1.70833 28.8843ZM12.8125 39.9884C10.4584 39.9884 8.54167 38.0734 8.54167 35.7176C8.54167 33.3618 10.4584 31.4468 12.8125 31.4468C15.1666 31.4468 17.0833 33.3618 17.0833 35.7176C17.0833 38.0734 15.1666 39.9884 12.8125 39.9884Z"
                        fill="#8E7E7A"
                      />
                      <path
                        d="M40.1458 34.8634H35.875V8.38428C35.875 4.1459 32.4259 0.696777 28.1875 0.696777H7.6875C3.44913 0.696777 0 4.1459 0 8.38428V28.8843C0 32.8544 3.02375 36.1293 6.888 36.5308C7.28604 39.4452 9.79046 41.6968 12.8125 41.6968C15.8345 41.6968 18.315 39.4657 18.7302 36.5718H40.1458C40.6173 36.5718 41 36.1891 41 35.7176C41 35.2461 40.6173 34.8634 40.1458 34.8634ZM7.6875 2.40511H28.1875C31.4846 2.40511 34.1667 5.08719 34.1667 8.38428V22.9051H29.0417V8.38428C29.0417 7.91278 28.659 7.53011 28.1875 7.53011C27.716 7.53011 27.3333 7.91278 27.3333 8.38428V22.9051H22.2083V8.38428C22.2083 7.91278 21.8257 7.53011 21.3542 7.53011C20.8827 7.53011 20.5 7.91278 20.5 8.38428V22.9051H15.375V8.38428C15.375 7.91278 14.9923 7.53011 14.5208 7.53011C14.0493 7.53011 13.6667 7.91278 13.6667 8.38428V22.9051H8.54167V8.38428C8.54167 7.91278 8.159 7.53011 7.6875 7.53011C7.216 7.53011 6.83333 7.91278 6.83333 8.38428V22.9051H1.70833V8.38428C1.70833 5.08719 4.39042 2.40511 7.6875 2.40511ZM1.70833 28.8843V24.6134H34.1667V34.8634H18.7302C18.315 31.9695 15.8192 29.7384 12.8125 29.7384C9.82463 29.7384 7.339 31.9439 6.90167 34.8122C3.97529 34.4261 1.70833 31.9149 1.70833 28.8843ZM12.8125 39.9884C10.4584 39.9884 8.54167 38.0734 8.54167 35.7176C8.54167 33.3618 10.4584 31.4468 12.8125 31.4468C15.1666 31.4468 17.0833 33.3618 17.0833 35.7176C17.0833 38.0734 15.1666 39.9884 12.8125 39.9884Z"
                        stroke="#8E7E7A"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2682_5264">
                        <rect
                          width="41"
                          height="41"
                          fill="white"
                          transform="translate(0 0.696777)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <Stack direction={"column"} justifyContent={"flex-start"}>
                    <Typography style={{ fontSize: "16px" }}>
                      Remorque
                    </Typography>
                    <Typography style={{ fontSize: "12px" }}>
                      Obligation d’assurer remorque.
                    </Typography>
                  </Stack>
                </Stack>
                <Box flexGrow={1} />
                <Stack direction={"row"}>
                  <Typography style={{ fontSize: "14px" }}>30€</Typography>
                  <Typography style={{ fontSize: "12px" }}>/mois</Typography>
                </Stack>
                <Button
                  variant={"contained"}
                  style={{ width: "fit-content" }}
                  size="small"
                  onClick={() =>
                    setFormulaDetails((prevDetails) => ({
                      ...prevDetails,
                      formulaSupp: {
                        ...prevDetails.formulaSupp,
                        remorque: "1",
                      },
                    }))
                  }
                >
                  Ajouter
                </Button>
              </Stack>
            </CardField>
            <CardField className={"SuggestionItem"} selected={false}>
              <Stack
                direction={"row"}
                spacing={5}
                alignItems={"baseline"}
                justifyContent={"center"}
              >
                <Stack direction={"row"} spacing={1}>
                  <svg
                    width="41"
                    height="38"
                    viewBox="0 0 41 38"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_2682_5291)">
                      <path
                        d="M3.1856 15.2737C2.44319 14.6206 2.1811 13.6622 2.50141 12.7723C2.8218 11.8825 3.65814 11.2462 4.6841 11.1118L13.6553 9.93535C13.8313 9.91229 13.9836 9.81249 14.0624 9.6684L18.0744 2.33235C18.5331 1.49335 19.4622 0.972168 20.499 0.972168C21.5357 0.972168 22.4648 1.49335 22.9236 2.33235L26.9356 9.66847C27.0144 9.81249 27.1666 9.91229 27.3428 9.93542L36.3138 11.1118C37.3397 11.2462 38.176 11.8825 38.4964 12.7723C38.8169 13.6622 38.5547 14.6206 37.8123 15.2737L31.3207 20.984C31.1933 21.0962 31.1351 21.2577 31.1652 21.416L32.6977 29.4792C32.8729 30.4013 32.4608 31.3157 31.6221 31.8657C30.7833 32.4157 29.692 32.4868 28.7745 32.0515L20.7506 28.2446C20.593 28.1698 20.4049 28.1698 20.2472 28.2446L12.2232 32.0515C11.8241 32.2409 11.3922 32.3344 10.9625 32.3344C10.4043 32.3344 9.8497 32.1765 9.37588 31.8656C8.53714 31.3156 8.12498 30.4011 8.30027 29.4791L9.83272 21.416C9.86275 21.2576 9.80462 21.0961 9.67721 20.984L3.1856 15.2737ZM11.9645 21.7461L10.432 29.8093C10.3808 30.0792 10.5654 30.233 10.6471 30.2866C10.729 30.3401 10.9482 30.4511 11.2166 30.3236L19.2406 26.5168C19.6345 26.33 20.0666 26.2364 20.4988 26.2364C20.931 26.2364 21.3632 26.33 21.757 26.5167L29.7809 30.3235C30.0495 30.4509 30.2687 30.34 30.3504 30.2864C30.4322 30.2329 30.6168 30.079 30.5655 29.8092L29.0331 21.7461C28.8826 20.9544 29.1732 20.1471 29.8107 19.5863L36.3022 13.876C36.5195 13.6848 36.4703 13.4624 36.439 13.3757C36.4079 13.2889 36.3027 13.0829 36.0025 13.0436L27.0316 11.8672C26.1508 11.7517 25.3897 11.2527 24.9957 10.5323L20.9837 3.19621C20.8494 2.95073 20.5999 2.92421 20.4987 2.92421C20.3978 2.92421 20.1482 2.95066 20.0139 3.19621V3.19629L16.0019 10.5324C15.608 11.2528 14.8469 11.7517 13.9661 11.8672L4.99504 13.0436C4.69483 13.0829 4.58977 13.289 4.55854 13.3758C4.52731 13.4626 4.47806 13.685 4.69539 13.8761L11.1869 19.5864C11.8243 20.1471 12.1149 20.9544 11.9645 21.7461Z"
                        fill="#8E7E7A"
                      />
                      <path
                        d="M7.86509 2.33171C8.34844 2.0149 9.0247 2.11159 9.37576 2.54764L10.5238 3.97359C10.8749 4.40964 10.7678 5.02006 10.2846 5.33688C10.0925 5.46269 9.87008 5.52332 9.64971 5.52332C9.31522 5.52332 8.98546 5.38378 8.7739 5.12102L7.62581 3.69507C7.27475 3.25895 7.38182 2.64852 7.86509 2.33171Z"
                        fill="#8E7E7A"
                      />
                      <path
                        d="M30.4813 3.96787L31.6294 2.54192C31.9804 2.10602 32.6567 2.00932 33.1401 2.32599C33.6233 2.6428 33.7304 3.25323 33.3793 3.68928L32.2313 5.11523C32.0196 5.3782 31.6898 5.51768 31.3553 5.51768C31.1348 5.51768 30.9124 5.45704 30.7205 5.33116C30.2373 5.01442 30.1302 4.40399 30.4813 3.96787Z"
                        fill="#8E7E7A"
                      />
                      <path
                        d="M37.032 22.87C37.2166 22.3574 37.8267 22.0768 38.3948 22.2433L40.2524 22.7881C40.8206 22.9547 41.1313 23.5054 40.9468 24.018C40.7983 24.4305 40.3743 24.6927 39.9184 24.6927C39.8077 24.6927 39.695 24.6772 39.5839 24.6446L37.7263 24.0998C37.1583 23.9333 36.8474 23.3827 37.032 22.87Z"
                        fill="#8E7E7A"
                      />
                      <path
                        d="M20.5005 33.7068C21.0978 33.7068 21.582 34.1438 21.582 34.6828V36.4455C21.582 36.9845 21.0978 37.4215 20.5005 37.4215C19.9032 37.4215 19.419 36.9845 19.419 36.4455V34.6828C19.419 34.1437 19.9031 33.7068 20.5005 33.7068Z"
                        fill="#8E7E7A"
                      />
                      <path
                        d="M0.747826 22.7895L2.6054 22.2448C3.17331 22.0783 3.78367 22.3587 3.96825 22.8714C4.15283 23.3842 3.84205 23.9348 3.27389 24.1013L1.41632 24.6461C1.30525 24.6786 1.19266 24.6941 1.08191 24.6941C0.625947 24.6941 0.201935 24.4318 0.0534697 24.0193C-0.13111 23.5067 0.179752 22.9561 0.747826 22.7895Z"
                        fill="#8E7E7A"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2682_5291">
                        <rect
                          width="41"
                          height="37"
                          fill="white"
                          transform="matrix(-1 0 0 1 41 0.696777)"
                        />
                      </clipPath>
                    </defs>
                  </svg>

                  <Stack direction={"column"} justifyContent={"flex-start"}>
                    <Typography style={{ fontSize: "14px" }}>
                      Indemnisation +
                    </Typography>
                    <Typography style={{ fontSize: "12px" }}>
                      Obligation d’assurer remorque.
                    </Typography>
                  </Stack>
                </Stack>
                <Box flexGrow={1} />
                <Stack direction={"row"}>
                  <Typography style={{ fontSize: "16px" }}>30€</Typography>
                  <Typography style={{ fontSize: "12px" }}>/mois</Typography>
                </Stack>
                <Button
                  variant={"contained"}
                  style={{ width: "fit-content" }}
                  size="small"
                  onClick={() =>
                    setFormulaDetails((prevDetails) => ({
                      ...prevDetails,
                      formulaSupp: {
                        ...prevDetails.formulaSupp,
                        indemnisationPlus: "O",
                      },
                    }))
                  }
                >
                  Ajouter
                </Button>
              </Stack>
            </CardField>
            <CardField className={"SuggestionItem"} selected={false}>
              <Stack direction={"column"} spacing={2}>
                <Stack
                  direction={"row"}
                  spacing={5}
                  alignItems={"baseline"}
                  justifyContent={"center"}
                >
                  <Stack direction={"row"} spacing={1}>
                    <svg
                      width="37"
                      height="40"
                      viewBox="0 0 37 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M35.4835 22.8877V21.2724C35.4835 20.8786 35.3904 20.4886 35.2163 20.1489L31.3337 12.3664L31.3463 12.3382C31.584 11.8044 31.7093 11.2162 31.7093 10.6354C31.7093 10.229 31.654 9.8371 31.5471 9.48173L29.9116 3.74457C29.3965 1.94993 27.7358 0.696777 25.872 0.696777H11.1272C9.26409 0.696777 7.60274 1.94993 7.08766 3.74519L5.4555 9.47313C5.34523 9.8371 5.28994 10.2284 5.28994 10.6354C5.28994 11.2162 5.41526 11.8044 5.65115 12.3339L5.66558 12.3664L1.78635 20.1427C1.60882 20.4886 1.51575 20.8786 1.51575 21.2724V22.8877C1.14134 23.1061 0.886719 23.5075 0.886719 23.971V27.7452C0.886719 28.2086 1.14134 28.6101 1.51575 28.8285V30.2613C1.51575 33.1706 3.72166 35.5715 6.54801 35.8854V37.8097C6.54801 38.85 7.39481 39.6968 8.43511 39.6968H10.9512C11.9915 39.6968 12.8383 38.85 12.8383 37.8097V35.9226H24.1609V37.8097C24.1609 38.85 25.0077 39.6968 26.048 39.6968H28.5641C29.6044 39.6968 30.4512 38.85 30.4512 37.8097V35.8854C33.2776 35.5715 35.4835 33.1706 35.4835 30.2613V28.8285C35.8579 28.6101 36.1125 28.2086 36.1125 27.7452V23.971C36.1125 23.5075 35.8579 23.1061 35.4835 22.8877ZM34.8545 25.229H33.5964V26.4871H34.8545V27.7452H30.297L30.6552 26.4871H32.3383V25.229H31.013L31.3708 23.971H34.8545V25.229ZM2.14478 27.7452V26.4871H3.40285V25.229H2.14478V23.971H5.62596L5.9844 25.229H4.66091V26.4871H6.34284L6.70158 27.7452H2.14478ZM8.38596 17.458C8.65072 17.5965 8.94466 17.6807 9.25272 17.6807H27.7465C28.0546 17.6807 28.3491 17.5971 28.6133 17.4586L30.1972 21.0184C30.3631 21.3919 30.4512 21.8047 30.4512 22.2098C30.4512 22.4933 30.4135 22.7627 30.3413 23.0041L29.4764 26.0381C29.1247 25.5328 28.5546 25.229 27.9351 25.229V24.6C27.9351 23.4356 27.2923 22.4291 26.3493 21.8852C26.5619 21.5774 26.677 21.2119 26.677 20.8258C26.677 19.7855 25.8302 18.9387 24.7899 18.9387H23.5319C23.0217 18.9387 22.5469 19.1374 22.2059 19.4885C21.8441 19.8414 21.6448 20.3156 21.6448 20.8258C21.6448 21.2183 21.7655 21.5832 21.9719 21.8858C21.0296 22.4291 20.3867 23.4362 20.3867 24.6V25.229H16.6125V24.6C16.6125 23.4356 15.9697 22.4291 15.0267 21.8852C15.2393 21.5774 15.3545 21.2119 15.3545 20.8258C15.3545 19.7855 14.5077 18.9387 13.4674 18.9387H12.2093C11.6991 18.9387 11.2243 19.1374 10.8834 19.4885C10.5215 19.8414 10.3222 20.3156 10.3222 20.8258C10.3222 21.2183 10.4429 21.5832 10.6493 21.8858C9.70699 22.4291 9.06414 23.4362 9.06414 24.6V25.2346C8.44708 25.2493 7.87917 25.5561 7.54132 26.0682L7.53548 26.0765L6.6896 23.1048L6.66012 23.0099C6.58579 22.7627 6.54801 22.4933 6.54801 22.2098C6.54801 21.8041 6.63616 21.3919 6.80202 21.0184L8.38596 17.458ZM22.9028 20.8258C22.9028 20.6578 22.967 20.503 23.0954 20.3774C23.2091 20.2616 23.3639 20.1968 23.5319 20.1968H24.7899C25.1364 20.1968 25.419 20.4787 25.419 20.8258C25.419 20.9938 25.3548 21.1486 25.2264 21.2742C25.1128 21.39 24.958 21.4548 24.7899 21.4548H23.5319C23.1854 21.4548 22.9028 21.1729 22.9028 20.8258ZM23.5319 22.7129H24.7899C25.8302 22.7129 26.677 23.5597 26.677 24.6V25.229H21.6448V24.6C21.6448 23.5597 22.4916 22.7129 23.5319 22.7129ZM11.5803 20.8258C11.5803 20.6578 11.6445 20.503 11.7728 20.3774C11.8865 20.2616 12.0413 20.1968 12.2093 20.1968H13.4674C13.8138 20.1968 14.0964 20.4787 14.0964 20.8258C14.0964 20.9938 14.0322 21.1486 13.9038 21.2742C13.7902 21.39 13.6354 21.4548 13.4674 21.4548H12.2093C11.8628 21.4548 11.5803 21.1729 11.5803 20.8258ZM12.2093 22.7129H13.4674C14.5077 22.7129 15.3545 23.5597 15.3545 24.6V25.229H10.3222V24.6C10.3222 23.5597 11.169 22.7129 12.2093 22.7129ZM8.58929 26.7638C8.70355 26.5909 8.89736 26.4871 9.10806 26.4871H27.9351C28.1452 26.4871 28.3402 26.5909 28.456 26.7663L29.0252 27.6186L28.7011 28.7541C28.3415 30.0122 27.1771 30.8903 25.872 30.8903H11.1272C9.82217 30.8903 8.6584 30.0122 8.29812 28.7554L7.98729 27.6647L8.58929 26.7638ZM34.0934 20.7165C34.1794 20.8851 34.2254 21.0767 34.2254 21.2724V22.7129H31.674C31.6943 22.548 31.7093 22.3815 31.7093 22.2098C31.7093 21.629 31.584 21.0408 31.3463 20.5076L29.5304 16.4257L30.6715 13.8567L34.0934 20.7165ZM21.5308 1.95484L21.1107 4.47097H15.888L15.4678 1.95484H21.5308ZM6.66257 9.82727L8.2972 4.09103C8.65779 2.83297 9.82217 1.95484 11.1272 1.95484H14.1925L14.402 3.21291H11.2845C10.438 3.21291 9.68948 3.78266 9.46343 4.60059L7.95381 10.131C7.83249 10.5801 7.87917 11.0555 8.0865 11.4708L9.41398 14.1178C9.73586 14.7631 10.3833 15.1645 11.1024 15.1645H22.9028V13.9065H11.1024C10.8625 13.9065 10.6469 13.7719 10.5387 13.5548L9.21126 10.9072C9.14154 10.7674 9.12649 10.6129 9.16795 10.4612L10.677 4.93322C10.7525 4.66171 11.0022 4.47097 11.2845 4.47097H14.6115L14.821 5.72904H22.1789L22.3884 4.47097H25.7154C25.9976 4.47097 26.2473 4.66171 26.3223 4.932L27.8313 10.46C27.8728 10.6129 27.8577 10.7674 27.7886 10.9066L26.4605 13.556C26.3518 13.7719 26.1368 13.9065 25.8969 13.9065H24.1609V15.1645H25.8969C26.6165 15.1645 27.2634 14.7631 27.5853 14.119L28.9134 11.4702C29.1201 11.0555 29.1674 10.5801 29.0454 10.1298L27.5349 4.59936C27.3098 3.78266 26.5612 3.21291 25.7148 3.21291H22.5972L22.8067 1.95484H25.872C27.1771 1.95484 28.3408 2.83297 28.7011 4.09042L30.3391 9.83587C30.4135 10.0825 30.4512 10.3519 30.4512 10.6354C30.4512 11.0411 30.3631 11.4533 30.1951 11.8302L28.3326 16.0251L28.2868 16.1075C28.1771 16.3019 27.9704 16.4226 27.7465 16.4226H9.25272C9.02943 16.4226 8.82211 16.3019 8.71246 16.1075L6.80202 11.8262C6.63616 11.4533 6.54801 11.0405 6.54801 10.6354C6.54801 10.3519 6.58579 10.0825 6.66257 9.82727ZM2.90896 20.71L6.32779 13.8567L7.46883 16.425L5.65299 20.507C5.41526 21.0408 5.28994 21.629 5.28994 22.2098C5.28994 22.3809 5.30499 22.5474 5.32527 22.7129H2.77382V21.2724C2.77382 21.0767 2.81989 20.8851 2.90896 20.71ZM11.5803 37.8097C11.5803 38.1568 11.2977 38.4387 10.9512 38.4387H8.43511C8.08865 38.4387 7.80607 38.1568 7.80607 37.8097V35.9226H11.5803V37.8097ZM28.5641 38.4387H26.048C25.7015 38.4387 25.419 38.1568 25.419 37.8097V35.9226H29.1932V37.8097C29.1932 38.1568 28.9106 38.4387 28.5641 38.4387ZM29.8222 34.6645H7.17704C4.74906 34.6645 2.77382 32.6887 2.77382 30.2613V29.0032H7.06002L7.08766 29.1006C7.60274 30.8952 9.26347 32.1484 11.1272 32.1484H25.872C27.7352 32.1484 29.3965 30.8952 29.9116 29.0994L29.9392 29.0032H34.2254V30.2613C34.2254 32.6887 32.2502 34.6645 29.8222 34.6645Z"
                        fill="#8E7E7A"
                      />
                    </svg>

                    <Stack direction={"column"} justifyContent={"flex-start"}>
                      <Typography style={{ fontSize: "14px" }}>
                        Contenu et équipement du véhicule
                      </Typography>
                      <Typography style={{ fontSize: "12px" }}>
                        Obligation d’assurer remorque.
                      </Typography>
                    </Stack>
                  </Stack>
                  <Box flexGrow={1} />
                  <Stack direction={"row"}>
                    <Typography style={{ fontSize: "16px" }}>30€</Typography>
                    <Typography style={{ fontSize: "12px" }}>/mois</Typography>
                  </Stack>
                  <Button
                    variant={"contained"}
                    style={{ width: "fit-content" }}
                    size="small"
                    onClick={() => {
                      setFormulaDetails((prevDetails) => ({
                        ...prevDetails,
                        formulaSupp: {
                          ...prevDetails.formulaSupp,
                          contenuEquipVeh: contenuEquip,
                        },
                      }));
                    }}
                  >
                    Ajouter
                  </Button>
                </Stack>
                <Container>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    defaultValue={1}
                  >
                    {contenuEquipVehOptions?.map((o, index) => (
                      <FormControlLabel
                        key={index}
                        value={o.code}
                        control={<Radio size="small" />}
                        onChange={() => setContenuEquip(o.code)}
                        label={<div variant="h5">{o.libelle}</div>}
                      />
                    ))}
                  </RadioGroup>
                </Container>
              </Stack>
            </CardField>
          </Stack>
        )
      )}
    </Stack>
  );
};

export default Component;
